import { Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import { normalizeTransactionGlLines, TransactionGlLine } from '../../models/transaction-gl.model';

@Component({
	selector: 'app-transaction-gl-table',
	templateUrl: './transaction-gl-table.component.html',
	styleUrls: ['./transaction-gl-table.component.scss'],
})
export class TransactionGlTableComponent implements OnChanges {
	@Input() lines: TransactionGlLine[] | null | undefined;
	/** Used for filenames, PDF heading, Excel; optional on-screen heading below parent chrome */
	@Input() exportTitle = '';
	@Input() showExportActions = true;
	/** When false, exportTitle is still used for exports but no extra screen heading is shown (e.g. dialog already has a title). */
	@Input() showScreenHeading = true;

	@ViewChild('pdfExportRoot', { static: false }) pdfExportRoot: ElementRef<HTMLElement> | undefined;

	normalized: TransactionGlLine[] = [];
	debitTotal = 0;
	creditTotal = 0;
	balanced = false;
	empty = false;
	exportingPdf = false;

	constructor(
		private globalSearchService: GlobalSearchService,
		private printService: PrintService
	) {}

	ngOnChanges(): void {
		this.normalized = normalizeTransactionGlLines(this.lines);
		this.empty = this.normalized.length === 0;
		this.debitTotal = this.normalized.reduce((s, r) => s + (Number(r.debit) || 0), 0);
		this.creditTotal = this.normalized.reduce((s, r) => s + (Number(r.credit) || 0), 0);
		this.balanced = Math.abs(this.debitTotal - this.creditTotal) < 0.001;
	}

	exportXlsx(): void {
		if (this.empty) {
			return;
		}
		const rows = this.normalized.map((line) => ({
			Period: line.period,
			Date: line.date,
			'Account code': line.accountcode || '',
			'GL account': line.account,
			Description: line.description,
			Debits: line.debit > 0 ? line.debit : '',
			Credits: line.credit > 0 ? line.credit : '',
			Posted: line.posted,
		}));
		rows.push({
			Period: '',
			Date: '',
			'Account code': '',
			'GL account': '',
			Description: 'Totals',
			Debits: this.debitTotal,
			Credits: this.creditTotal,
			Posted: '',
		});
		this.globalSearchService.exportJsonToExcel(rows, this.fileBase());
	}

	exportPdf(): void {
		if (this.empty || !this.pdfExportRoot?.nativeElement) {
			return;
		}
		this.exportingPdf = true;
		const filename = `${this.fileBase()}.pdf`;
		const titleBlock = this.exportTitle
			? `<h2 style="font-size:15px;margin:0 0 10px;font-family:sans-serif">${this.escapeHtml(this.exportTitle)}</h2>`
			: '';
		const encoded = this.globalSearchService.base64encode(titleBlock + this.pdfExportRoot.nativeElement.innerHTML);
		this.printService.pdf({ content: encoded, filename }).subscribe({
			next: (result: any) => {
				this.globalSearchService.downloadPdf(result.content, filename);
				this.exportingPdf = false;
			},
			error: () => {
				this.exportingPdf = false;
			},
		});
	}

	private fileBase(): string {
		const raw = (this.exportTitle || 'GL_transactions')
			.replace(/[^a-zA-Z0-9]+/g, '_')
			.replace(/^_|_$/g, '');
		return (raw || 'GL_transactions') + '_' + new Date().toISOString().slice(0, 10);
	}

	private escapeHtml(text: string): string {
		return String(text)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}
}
